// @ts-check

/**
 * @file Renders the user-role editor screen.
 */

import {Form, FormInputs} from "@kaspernj/api-maker/build/form"
import Text from "@kaspernj/api-maker/build/utils/text"
import {useLocalSearchParams, useRouter} from "expo-router"
import {memo, useEffect} from "react"
import PropTypesExact from "prop-types-exact"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"

import Button from "../../components/button"
import FormSelect from "../../components/form/form-select"
import UsersSelect from "../../components/users/select"
import {UserRole} from "../../frontend-models/user-role"
import errorMessage from "../../shared/error-message"
import {optionalRouteParam} from "../../shared/route-params"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import {currentLocale, resolveRedirectUrl, ROLE_OPTIONS, TRANSLATIONS, translateRole} from "./shared"
import {useScreenLoading} from "../../components/board-layout/screen-loading"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{kind: "error" | "success", text: string} | null} UserRoleEditorStatus */

/** @typedef {{[key: string]: unknown}} UserRolesEditScreenProps */

/**
 * @typedef {object} UserRolesEditScreenState
 * @property {boolean} loading Whether the edit payload is loading.
 * @property {string} role Initial selected role value.
 * @property {boolean} saving Whether the save command is running.
 * @property {UserRoleEditorStatus} status Status message shown above the form.
 * @property {UserRole | null} userRole User role being edited.
 * @property {string} userRoleUserId Initial selected user id.
 */

/**
 * User role editor screen migrated from the legacy Rails/React app.
 * @augments {ShapeComponent<UserRolesEditScreenProps, UserRolesEditScreenState>}
 */
class UserRolesEditScreen extends ShapeComponent {
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {"da" | "de" | "en"} */
  locale
  /** @type {typeof TRANSLATIONS.en} */
  translation
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  form = new FormInputs({})
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {UserRolesEditScreenState} */
  state = {
    loading: true,
    role: "",
    saving: false,
    status: null,
    userRole: null,
    userRoleUserId: ""
  }
  loadRequestId = 0
  static propTypes = PropTypesExact({})

  /**
   * Initializes router context and loads editor data.
   * @returns {void} No return value.
   */
  setup() {
    useScreenLoading(this.s.loading)
    const params =
      /** @type {Record<string, string | string[]>} */ useLocalSearchParams()
    const router = useRouter()
    const locale = currentLocale()
    const translation = TRANSLATIONS[locale]
    this.locale = locale
    this.params = params
    this.router = router
    this.translation = translation
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      void this.loadEditor()
    }, [params.id, params.user_id, translation.forbidden])
  }

  /**
   * Renders the role editor form.
   * @returns {ReactNode} User-role editor content.
   */
  render() {
    const title = optionalRouteParam(this.tt.params, "id", "User role ID")
      ? this.tt.translation.editUserRole
      : this.tt.translation.addNewUserRole
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    if (this.s.loading) {
      return (
        <View
          style={styles.loadingScreen ||= {
            flex: 1
          }}
          testID="userRoleEditorScreen"
        >
          <Text
            style={styles.loadingTitle ||= {
              fontSize: 24,
              fontWeight: 700,
              marginBottom: 12
            }}
            testID="userRoleEditorScreen/title"
          >
            {title}
          </Text>
          <Text
            style={styles[`loadingText-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 16
            }}
            testID="userRoleEditorScreen/loadingText"
          >
            {this.tt.translation.loadingEditor}
          </Text>
        </View>
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="userRoleEditorScreen"
      >
        <Text
          style={styles.title ||= {
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="userRoleEditorScreen/title"
        >
          {title}
        </Text>
        <Form
          form={this.tt.form}
          onSubmit={this.tt.onSavePress}
        >
          <View
            style={styles.form ||= {
              gap: 16,
              maxWidth: 520,
              width: "100%"
            }}
            testID="userRoleEditorScreen/form"
          >
            {this.s.status &&
              <Text
                style={styles[`status-${themeName}-${this.s.status.kind}`] ||= {
                  color: this.s.status.kind === "error" ? colors.dangerText : colors.successText,
                  fontSize: 14,
                  fontWeight: 600
                }}
                testID="userRoleEditorScreen/status"
              >
                {this.s.status.text}
              </Text>
            }
            <FormSelect
              defaultValue={this.s.role}
              label={this.tt.translation.role}
              name="role"
              onChangeValue={this.tt.onRoleChangeValue}
              options={this.tt.roleOptions}
              search={false}
              testID="userRoleEditorScreen/roleInput"
            />
            <UsersSelect
              defaultValue={this.s.userRoleUserId}
              label={this.tt.translation.selectUser}
              name="userId"
              onChangeValue={this.tt.onUserChangeValue}
              testID="userRoleEditorScreen/userInput"
            />
            <View
              style={styles.saveButtonRow ||= {
                alignSelf: "flex-start"
              }}
            >
              <Button
                disabled={this.s.saving}
                iconName="floppy-disk"
                label={this.s.saving ? this.tt.translation.saving : this.tt.translation.save}
                onPress={this.tt.onSavePress}
                testID="userRoleEditorScreen/saveButton"
                variant="neutral"
              />
            </View>
          </View>
        </Form>
      </View>
    )
  }

  /**
   * Loads current form values from frontend models.
   * @returns {Promise<void>} Resolves after editor state is updated.
   */
  async loadEditor() {
    const requestId = ++this.loadRequestId
    try {
      const userRoleID = optionalRouteParam(this.tt.params, "id", "User role ID")
      const userRole = userRoleID
        ? await UserRole.find(userRoleID)
        : null
      if (requestId !== this.loadRequestId) {
        return
      }
      this.s.role = userRole ? userRole.role() || "" : ""
      this.s.userRole = userRole
      this.s.userRoleUserId = userRole
        ? String(userRole.userId() || "")
        : this.initialUserId()
      this.s.loading = false
      this.s.saving = false
      this.s.status = null
    } catch (error) {
      if (requestId !== this.loadRequestId) {
        return
      }
      const message = errorMessage(error)
      this.s.loading = false
      this.s.status = {
        kind: "error",
        text: message === "Forbidden" ? this.tt.translation.forbidden : message
      }
    }
  }

  /**
   * Role options accepted by the user-role editor.
   * @returns {{text: string, value: string}[]} Select options.
   */
  roleOptions = () => ROLE_OPTIONS.map((option) => ({
    text: translateRole(this.tt.locale, option.value),
    value: option.value
  }))

  /**
   * User id from route params when adding a role from a user page.
   * @returns {string} Initial user id or empty string.
   */
  initialUserId() {
    return optionalRouteParam(this.tt.params, "user_id", "User ID") ?? ""
  }

  /**
   * Stores the selected role from HayaSelect.
   * @param {(string | number)[] | string | number | undefined} value Selected role value.
   * @returns {void} No return value.
   */
  onRoleChangeValue = (value) => {
    this.s.role = selectedSelectValue(value)
    this.s.status = null
  }

  /**
   * Stores the selected user from HayaSelect.
   * @param {(string | number)[] | string | number | undefined} value Selected user id.
   * @returns {void} No return value.
   */
  onUserChangeValue = (value) => {
    this.s.userRoleUserId = selectedSelectValue(value)
    this.s.status = null
  }

  /**
   * Saves the selected user role.
   * @returns {Promise<void>} Resolves after save handling finishes.
   */
  onSavePress = async () => {
    const role = this.s.role
    const userID = this.s.userRoleUserId
    if (!role) {
      this.s.status = {
        kind: "error",
        text: this.tt.translation.chooseRole
      }
      return
    }
    if (!userID) {
      this.s.status = {
        kind: "error",
        text: this.tt.translation.noUsers
      }
      return
    }
    if (!userID.trim()) {
      this.s.status = {
        kind: "error",
        text: this.tt.translation.noUsers
      }
      return
    }
    this.s.saving = true
    this.s.status = null
    let didRedirect = false
    try {
      const userId = userID

      if (this.s.userRole) {
        await this.s.userRole.update({role, userId})
      } else {
        await UserRole.create({role, userId})
      }
      didRedirect = true
      const redirectUrl = resolveRedirectUrl(this.tt.params, `/users/${userID}/user-roles?saved=1`)
      if (redirectUrl.startsWith("http://") || redirectUrl.startsWith("https://")) {
        this.tt.router.replace(redirectUrl)
      } else {
        this.tt.router.push(redirectUrl)
      }
    } catch (error) {
      const message = errorMessage(error)
      this.s.status = {
        kind: "error",
        text: message.includes("Role has already been taken")
          ? this.tt.translation.theUserAlreadyHasThatRole
          : message
      }
    } finally {
      if (!didRedirect) {
        this.s.saving = false
      }
    }
  }
}

/** @type {NamedExoticComponent<UserRolesEditScreenProps>} */
const UserRolesEditScreenComponent = memo(shapeComponent(UserRolesEditScreen))

export default UserRolesEditScreenComponent

/**
 * Extracts a single select value from HayaSelect callback payloads.
 * @param {(string | number)[] | string | number | undefined} value Selected value payload.
 * @returns {string} First selected value or empty string.
 */
function selectedSelectValue(value) {
  if (Array.isArray(value)) {
    return value.length > 0 ? String(value[0]) : ""
  }
  if (typeof value === "string" || typeof value === "number") {
    return String(value)
  }
  return ""
}
